import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "sonner";

interface VotingSectionProps {
  listingId: string;
  initialVoteCount: number;
}

export const VotingSection = ({ listingId, initialVoteCount }: VotingSectionProps) => {
  const { user } = useAuth();
  const [voteCount, setVoteCount] = useState(initialVoteCount || 0);
  const [hasVoted, setHasVoted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      checkUserVote();
    }
  }, [user, listingId]);

  const checkUserVote = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("listing_votes")
        .select("id")
        .eq("listing_id", listingId)
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Error checking vote:", error);
        return;
      }

      setHasVoted(!!data);
    } catch (error) {
      console.error("Error checking vote:", error);
    }
  };

  const handleVote = async () => {
    if (!user) {
      toast.error("Debes iniciar sesión para votar");
      return;
    }

    setIsLoading(true);

    try {
      if (hasVoted) {
        // Remove vote
        const { error } = await supabase
          .from("listing_votes")
          .delete()
          .eq("listing_id", listingId)
          .eq("user_id", user.id);

        if (error) throw error;

        setHasVoted(false);
        setVoteCount((prev) => prev - 1);
        toast.success("Voto eliminado");
      } else {
        // Add vote
        const { error } = await supabase
          .from("listing_votes")
          .insert({
            listing_id: listingId,
            user_id: user.id,
          });

        if (error) throw error;

        setHasVoted(true);
        setVoteCount((prev) => prev + 1);
        toast.success("¡Voto añadido!");
      }
    } catch (error: any) {
      console.error("Error voting:", error);
      toast.error("Error al votar");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant="default"
      size="lg"
      onClick={handleVote}
      disabled={isLoading}
      className={`gap-3 text-lg px-8 py-6 shadow-xl transition-all hover-scale ${
        hasVoted 
          ? "bg-primary hover:bg-primary/90" 
          : "bg-white hover:bg-white/90 text-primary border-2 border-primary"
      }`}
    >
      <Heart className={`w-6 h-6 ${hasVoted ? "fill-current text-white" : "text-primary"}`} />
      <div className="flex items-center gap-2">
        <span className="font-bold text-xl">{voteCount}</span>
        <span className="font-semibold">Votar</span>
      </div>
    </Button>
  );
};
